import { useAuth, authorizedFetch } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface MacroTotals {
  energy_kcal?: string | null
  protein_g?: string | null
  carbohydrate_g?: string | null
  fat_g?: string | null
  fiber_g?: string | null
  sugar_g?: string | null
  sodium_mg?: string | null
}

export interface MealLinePayload {
  id?: string
  line_type: 'manual' | 'vision_estimated' | string
  sort_order?: number
  name?: string
  quantity?: string
  unit?: string
  energy_kcal?: string | null
  protein_g?: string | null
  carbohydrate_g?: string | null
  fat_g?: string | null
  fiber_g?: string | null
  sugar_g?: string | null
  sodium_mg?: string | null
  vision_metadata?: Record<string, unknown>
}

export interface MealPayload {
  id: string
  event_type?: string
  consumed_at: string
  notes: string
  display_title: string
  photo_id?: string | null
  macros_total: MacroTotals
  lines: MealLinePayload[]
}

export interface DailySummaryPayload {
  day_start_utc: string
  day_end_utc: string
  macros_total: MacroTotals
  meals: Array<{
    id: string
    consumed_at: string
    display_title: string
    macros_total: MacroTotals
  }>
}

export interface NutritionFilters {
  day?: string
  start_at?: string
  end_at?: string
  limit?: number
}

export function useNutritionMeals(filters: NutritionFilters = {}) {
  const { token } = useAuth()
  const queryKey = ['nutrition', 'meals', filters]

  const q = useQuery({
    queryKey,
    queryFn: async (): Promise<MealPayload[]> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams()
      if (filters.day) params.append('day', filters.day)
      if (filters.start_at) params.append('start_at', filters.start_at)
      if (filters.end_at) params.append('end_at', filters.end_at)
      if (filters.limit != null) params.append('limit', String(filters.limit))
      const qs = params.toString()
      const url = `/api/nutrition/${qs ? `?${qs}` : ''}`
      const response = await authorizedFetch(url)
      if (!response.ok) throw new Error('Failed to fetch meals')
      return response.json()
    },
    enabled: !!token,
    staleTime: 15 * 1000,
  })

  return {
    meals: q.data ?? [],
    isLoading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  }
}

export function useNutritionDailySummary(day: string) {
  const { token } = useAuth()

  const q = useQuery({
    queryKey: ['nutrition', 'daily-summary', day],
    queryFn: async (): Promise<DailySummaryPayload> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams({ day })
      const response = await authorizedFetch(`/api/nutrition/daily-summary?${params}`)
      if (!response.ok) throw new Error('Failed to fetch nutrition summary')
      return response.json()
    },
    enabled: !!token && !!day.trim(),
    staleTime: 15 * 1000,
  })

  return {
    summary: q.data,
    isLoading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  }
}

function invalidateNutritionQueries(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['nutrition'] })
}

export function useCreateNutritionMeal() {
  const { token } = useAuth()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (body: Record<string, unknown>): Promise<MealPayload> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch('/api/nutrition/', {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}))
        throw new Error((errBody as { error?: string }).error || 'Failed to create meal')
      }
      return response.json()
    },
    onSuccess: () => {
      invalidateNutritionQueries(qc)
    },
  })
}

export function useUpdateNutritionMeal() {
  const { token } = useAuth()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({
      mealId,
      body,
    }: {
      mealId: string
      body: Record<string, unknown>
    }): Promise<MealPayload> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`/api/nutrition/${mealId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      })
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}))
        throw new Error((errBody as { error?: string }).error || 'Failed to update meal')
      }
      return response.json()
    },
    onSuccess: () => {
      invalidateNutritionQueries(qc)
    },
  })
}

export function useDeleteNutritionMeal() {
  const { token } = useAuth()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (mealId: string) => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`/api/nutrition/${mealId}`, { method: 'DELETE' })
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}))
        throw new Error((errBody as { error?: string }).error || 'Failed to delete meal')
      }
      return mealId
    },
    onSuccess: () => {
      invalidateNutritionQueries(qc)
    },
  })
}

export function useNutritionEstimatePhoto() {
  const { token } = useAuth()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({
      mealId,
      fileAssetId,
      attachAsMealPhoto = true,
    }: {
      mealId: string
      fileAssetId: string
      attachAsMealPhoto?: boolean
    }) => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`/api/nutrition/estimate-photo/${mealId}`, {
        method: 'POST',
        body: JSON.stringify({
          file_asset_id: fileAssetId,
          attach_as_meal_photo: attachAsMealPhoto,
        }),
      })
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}))
        throw new Error((errBody as { error?: string }).error || 'Vision estimate failed')
      }
      return response.json()
    },
    onSuccess: () => {
      invalidateNutritionQueries(qc)
    },
  })
}
